import { Link, useNavigate } from "react-router-dom";
// import ChatIcon from "../../assets/Vector.png";
// import BellIcon from "../../assets/message (6) 1.png";
import { PiListLight } from "react-icons/pi";
import { ImCross } from "react-icons/im";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState, useMemo } from "react";
import { useMyContext } from "../../context/MyContext";
import shipmentWhite from "../../assets/shipmentWhite.png";
import orderHistory from "../../assets/orderHistory.png";
import orderHistoryWhite from "../../assets/orderHistoryWhite.png";
import dashboardWhite from "../../assets/dashboardWhite.png";
import clientWhite from "../../assets/clientWhite.png";
import client from "../../assets/client.png";
import setting from "../../assets/setting.png";
import settingWhite from "../../assets/settingWhite.png";
import helpwhite from "../../assets/helpwhite.png";
import quotewhite from "../../assets/quotewhite.png";
import shipwhite from "../../assets/shipwhite.png";
import trackwhite from "../../assets/trackwhite.png";
import shipment from "../../assets/shipments.png";
import dashboard from "../../assets/dashboard.png";
import HeaderDropdown from "./HeaderDropdown";
import Notifications from "./Notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../config/api";

// New Notification Bell Icon Component
const NotificationIcon = ({ className = "" }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`transition-all duration-300 hover:scale-110 ${className}`}
    >
      {/* Bell body */}
      <path
        d="M12 2C13.1 2 14 2.9 14 4C16.3 4.89 18 7.22 18 10V16L20 18V19H4V18L6 16V10C6 7.22 7.7 4.89 10 4C10 2.9 10.9 2 12 2Z"
        fill="currentColor"
        className="text-blue-600"
      />
      {/* Bell bottom */}
      <path
        d="M10 21C10 22.1 10.9 23 12 23C13.1 23 14 22.1 14 21"
        fill="currentColor"
        className="text-blue-600"
      />
    </svg>
  );
};

// New Chat Message Icon Component
const ChatIcon = ({ className = "" }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`transition-all duration-300 hover:scale-110 ${className}`}
    >
      {/* Chat bubble */}
      <path
        d="M4 4H20C21.1 4 22 4.9 22 6V16C22 17.1 21.1 18 20 18H6L2 22V6C2 4.9 2.9 4 4 4Z"
        fill="currentColor"
        className="text-blue-600"
      />
      {/* Chat dots */}
      <circle cx="8" cy="12" r="1.5" fill="white" />
      <circle cx="12" cy="12" r="1.5" fill="white" />
      <circle cx="16" cy="12" r="1.5" fill="white" />
    </svg>
  );
};

function Header({ title }) {
  const { user, notifications, setNotifications, token, welcomeTourActive, startWelcomeTour } = useMyContext();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChatNotifications, setShowChatNotifications] = useState(false);
  const notificationBoxRef = useRef(null);
  const notificationChatRef = useRef(null);
  const queryClient = useQueryClient();

  // Determine what role to send to API
  const getRequestRole = () => {
    return user?.role === "super admin" ? "admin" : user?.role;
  };

  // Optimized notification fetch function with error handling and timeout
  const fetchNotifications = async () => {
    if (!user?.uid) return [];

    try {
      // Single API call with consolidated backend logic to handle all roles
      const res = await api.post("/notifications/user", {
        userId: user.uid,
        role: user.role
      }, {
        headers: {
          role: user?.role,
          userId: user?.uid,
        },
        // timeout: 10000 // 10 second timeout
      });

      return res.data || [];
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Return cached data or empty array
      const cachedData = queryClient.getQueryData(["notifications", user?.uid]);
      return cachedData || [];
    }
  };

  // Optimized React Query with proper caching
  const { data: notificationData, isLoading } = useQuery(
    ["notifications", user?.uid, user?.role],
    fetchNotifications,
    {
      // Configure React Query for optimal performance
      refetchOnWindowFocus: false,
      // refetchInterval: user?.role?.includes("admin") ? 30000 : 45000, // Less frequent refreshes
      // staleTime: 20000,        // Data stays fresh longer
      refetchInterval: 10000, // Poll every 10 seconds instead of 30-45s
      staleTime: 5000,        // Consider data stale after 5s
      cacheTime: 300000,       // Cache data for 5 minutes 
      retry: 1,                // Only retry once
      retryDelay: 3000,        // Wait longer between retries
      // Success callback can be used for side effects
      onSuccess: (data) => {
        if (Array.isArray(data) && data.length > 0) {
          setNotifications(data);
        }
      },
      // Skip query if no user
      enabled: !!user?.uid
    }
  );

  // Create memoized filtered notifications to prevent unnecessary recalculations
  const filteredNotifications = useMemo(() => {
    const notifications = notificationData || [];
    if (!Array.isArray(notifications) || notifications.length === 0) return [];

    const today = new Date();
    const timeFrame = user?.role?.includes("admin") ? 14 : 7; // 14 days for admin, 7 for users
    const startDate = new Date(today.getTime() - timeFrame * 24 * 60 * 60 * 1000);

    return notifications
      .filter(notification => {
        const notificationDate = new Date(notification.date);
        return notificationDate >= startDate && notificationDate <= today;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [notificationData, user?.role]);

  // Memoize notification counts to prevent recalculations on every render
  const { notificationCount, chatCount } = useMemo(() => {
    // Notification types that aren't chat messages
    const notificationTypes = [
      "new-inquiry", "warehouse-shipments", "quote-accepted", "new-quote", "quotes-from-admin",
      "quote-cancelled", "updated-inquiry", "shipment-status-update",
      "invoice-generated", "todo-assigned", "quote-assigned", "task-completed",
      "new-estimate", "update-estimate", "estimate-generated", "estimation-accepted", "estimation-rejected",
      "external-estimate-created", "shipments-update", "invoice-edited"
    ];

    // Count notifications
    let notifCount = 0;
    let chatNotifCount = 0;

    filteredNotifications.forEach(n => {
      if (!n.read) {
        if (notificationTypes.includes(n.type) ||
          (n.type === "task-completed" && user?.role === "super admin")) {
          notifCount++;
        } else if (n.sender !== user.uid) {
          chatNotifCount++;
        }
      }
    });

    return {
      notificationCount: notifCount,
      chatCount: chatNotifCount
    };
  }, [filteredNotifications, user?.uid, user?.role]);

  const roleis = user?.role;
  const items =
    roleis === "admin"
      ? [
        {
          content: "Dashboard",
          src: dashboard,
          path: "/dashboard",
          active: false,
          activeSrc: dashboardWhite,
        },
        {
          content: "Quote",
          src: dashboard,
          path: "/quoteadmin",
          active: false,
          activeSrc: dashboardWhite,
        },
        {
          content: "Shipments",
          src: shipment,
          path: "/shipments",
          active: false,
          activeSrc: shipmentWhite,
        },
        {
          content: "Order History",
          src: orderHistory,
          path: "/orderhistory",
          active: false,
          activeSrc: orderHistoryWhite,
        },
        {
          content: "Clients",
          src: client,
          path: "/clients",
          active: false,
          activeSrc: clientWhite,
        },
        {
          content: "Admins",
          src: client,
          path: "/admins",
          active: false,
          activeSrc: clientWhite,
        },
        {
          content: "Settings",
          src: setting,
          path: "/settings",
          active: false,
          activeSrc: settingWhite,
        },
        {
          content: "Support & Help",
          src: "icon2",
          path: "/support&help",
          active: false,
          activeSrc: helpwhite,
        },
      ]
      : [
        {
          content: "Dashboard",
          src: dashboard,
          path: "/dashboard",
          active: false,
          activeSrc: dashboardWhite,
        },
        {
          content: "New Qoutes",
          src: "icon1",
          path: "/newquotes",
          active: false,
          activeSrc: quotewhite,
        },
        {
          content: "My Qoutes",
          src: "icon1",
          path: "/myqoutes",
          active: false,
          activeSrc: quotewhite,
        },
        {
          content: "Book Shipment",
          src: "icon4",
          path: "/bookshipment",
          active: false,
          activeSrc: shipwhite,
        },
        {
          content: "My Shipments",
          src: "icon3",
          path: "/trackshipment",
          active: false,
          activeSrc: trackwhite,
        },
        {
          content: "Shipment History",
          src: "icon1",
          path: "/shipmenthistory",
          active: false,
          activeSrc: shipwhite,
        },
        {
          content: "Support & Help",
          src: "icon2",
          path: "/support&help",
          active: false,
          activeSrc: helpwhite,
        },
        {
          content: "One Union TradeOS™",
          src: "icon2",
          path: "/oneunion-trade-os",
          active: false,
          activeSrc: trackwhite,
        },
        {
          content: "HS Compass™ by OUS",
          src: "icon2",
          path: "/hs-compass",
          active: false,
          activeSrc: trackwhite,
        },
      ];

  const [isMenuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => {
    setMenuOpen(!isMenuOpen);
  };
  const closeMenu = () => {
    setMenuOpen(false);
  };

  const handleOutsideClick = (e) => {
    if (
      (notificationBoxRef.current && !notificationBoxRef.current.contains(e.target)) &&
      (notificationChatRef.current && !notificationChatRef.current.contains(e.target))
    ) {
      setShowNotifications(false);
      setShowChatNotifications(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const markChatNotificationsAsRead = async () => {
    // Don't make API call if there are no unread chat notifications
    if (chatCount === 0) return;

    try {
      await api.put(`/notifications/chat/${user.uid}`, {
        role: user.role,
        isChatNotification: true
      }, {
        headers: { authorization: `Bearer ${token}` }
      });
      // Invalidate cache to reflect the updated read status
      queryClient.invalidateQueries(["notifications", user?.uid, user?.role]);
    } catch (error) {
      console.error("Error marking chat notifications as read:", error);
    }
  };

  const handleStartTour = () => {
    startWelcomeTour();
    navigate("/dashboard");
  };

  return (
    <>
      <div className="header-wrapper flex items-center justify-between h-16 px-5 bg-white border md:px-10 w-full ">
        <p className="text-xl font-bold">{title}</p>
        {/* hamburger */}
        <button
          onClick={toggleMenu}
          className="relative block p-2 border rounded-md sm:hidden"
        >
          <PiListLight size={28} />
        </button>
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onClick={closeMenu}
              className="z-[2] bg-black/90 text-white w-full  absolute top-0 left-0 rounded-md  p-3 shadow-lg  text-bold text-[12px]  uppercase border flex flex-col"
            >
              <div className="flex flex-col items-center justify-center gap-y-4">
                <div
                  onClick={closeMenu}
                  className="flex items-end justify-end w-full"
                >
                  <ImCross />
                </div>
                {items?.map((item, index) => (
                  <Link key={index} to={item?.path}>{item?.content}</Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="items-center hidden gap-3 sm:flex justify-evenly ">
          {user?.role === "user" && !welcomeTourActive && (
            <button
              type="button"
              onClick={handleStartTour}
              className="px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 text-sm font-medium"
            >
              Take a Tour
            </button>
          )}
          <div ref={notificationBoxRef} className="relative">
            <div
              className="px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 transform hover:scale-105"
              onClick={(e) => {
                e.stopPropagation();
                setShowNotifications(!showNotifications);
                setShowChatNotifications(false);
              }}
            >
              <NotificationIcon />
            </div>
            {notificationCount > 0 && (
              <div className="size-6 absolute  -top-2 -right-2 rounded-full bg-red-500 text-white text-xs flex items-center justify-center cursor-pointer animate-pulse">
                <span>{notificationCount <= 99 ? notificationCount : "99+"}</span>
              </div>
            )}
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Notifications
                  notifications={filteredNotifications}
                  onClose={() => setShowNotifications(false)}
                  isLoading={isLoading}
                  type="notifications"
                  user={user}
                />
              </motion.div>
            )}

          </div>
          <div ref={notificationChatRef} className="relative">
            <div
              className="px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 transform hover:scale-105"
              onClick={(e) => {
                e.stopPropagation();
                setShowChatNotifications(!showChatNotifications);
                setShowNotifications(false);
                // if (chatCount > 0) markChatNotificationsAsRead();
              }}
            >
              <ChatIcon />
            </div>
            {chatCount > 0 && (
              <div className="size-6 absolute  -top-2 -right-2 rounded-full bg-red-500 text-white text-xs flex items-center justify-center cursor-pointer animate-pulse">
                <span>{chatCount <= 99 ? chatCount : "99+"}</span>
              </div>
            )}
            {showChatNotifications && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Notifications
                  notifications={filteredNotifications}
                  onClose={() => setShowChatNotifications(false)}
                  isLoading={isLoading}
                  type="chats"
                  user={user}
                />
              </motion.div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <HeaderDropdown user={user} />
            <div className="flex flex-col">
              <p className="text-sm">{user?.name}</p>
              <p className="text-xs">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
